import { FetchResult } from 'apollo-link';

import { SetMetrikaGoalsDataForStrategyEditorActionPayload } from '../actions/campaignsEdit';
import { getCampaignStrategyEditorId } from '../service/CampaignsEditHelpers/CampaignsEditHelpers';
import { EditCampaignData, EditCampaignValueField } from '../typings/campaignsEdit';
import { CampaignId, CampaignType } from '../typings/domain';
import { GoalForStrategy } from '../typings/goals';
import { GetOrCreateMetrikaGoalsByCounter } from '../typings/graphql-grid';
import {
    DnaStrategyType,
    Strategy,
    StrategyEditorInitData,
    StrategyForEditor,
    StrategyForEditorManual,
    StrategyForEditorOptimizeClicks,
    StrategyForEditorOptimizeCpa,
    StrategyForEditorRoi,
    StrategyManual,
    StrategyOptimizeClicks,
    StrategyOptimizeCpa,
    StrategyRoi
} from '../typings/strategy';

export function mapStrategyManualForEditor(strategy: StrategyManual): StrategyForEditorManual {
    return {
        ...strategy,
        dailyBudget: {
            inputVal: String(strategy.dailyBudget),
            numVal: strategy.dailyBudget
        }
    };
}

export function mapStrategyRoiForEditor(strategy: StrategyRoi): StrategyForEditorRoi {
    return {
        ...strategy,
        roiCoef: {
            inputVal: String(strategy.roiCoef),
            numVal: strategy.roiCoef
        },
        profitability: {
            inputVal: String(strategy.profitability),
            numVal: strategy.profitability
        },
        maxClickPrice: {
            inputVal: String(strategy.maxClickPrice),
            numVal: strategy.maxClickPrice
        },
        weeklyBudget: {
            inputVal: String(strategy.weeklyBudget),
            numVal: strategy.weeklyBudget
        }
    };
}

export function mapStrategyOptimizeCpaForEditor(strategy: StrategyOptimizeCpa): StrategyForEditorOptimizeCpa {
    return {
        ...strategy,
        avgCpaPrice: {
            inputVal: String(strategy.avgCpaPrice),
            numVal: strategy.avgCpaPrice
        },
        maxClickPrice: {
            inputVal: String(strategy.maxClickPrice),
            numVal: strategy.maxClickPrice
        },
        weeklyBudget: {
            inputVal: String(strategy.weeklyBudget),
            numVal: strategy.weeklyBudget
        }
    };
}

export function mapStrategyOptimizeClicksForEditor(strategy: StrategyOptimizeClicks): StrategyForEditorOptimizeClicks {
    return {
        ...strategy,
        avgClickPrice: {
            inputVal: String(strategy.avgClickPrice),
            numVal: strategy.avgClickPrice
        },
        weekBundle: {
            inputVal: String(strategy.weekBundle),
            numVal: strategy.weekBundle
        },
        maxClickPrice: {
            inputVal: String(strategy.maxClickPrice),
            numVal: strategy.maxClickPrice
        },
        weeklyBudget: {
            inputVal: String(strategy.weeklyBudget),
            numVal: strategy.weeklyBudget
        }
    };
}

export function mapStrategyManualForCampaignData(strategy: StrategyForEditorManual): StrategyManual {
    return {
        ...strategy,
        dailyBudget: strategy.dailyBudget.numVal
    };
}

export function mapStrategyRoiForCampaignData(strategy: StrategyForEditorRoi): StrategyRoi {
    return {
        ...strategy,
        roiCoef: strategy.roiCoef.numVal || 0,
        profitability: strategy.profitability.numVal || 0,
        maxClickPrice: strategy.maxClickPrice.numVal,
        weeklyBudget: strategy.weeklyBudget.numVal
    };
}

export function mapStrategyOptimizeCpaForCampaignData(strategy: StrategyForEditorOptimizeCpa): StrategyOptimizeCpa {
    return {
        ...strategy,
        avgCpaPrice: strategy.avgCpaPrice.numVal,
        maxClickPrice: strategy.maxClickPrice.numVal,
        weeklyBudget: strategy.weeklyBudget.numVal
    };
}

export function mapStrategyOptimizeClicksForCampaignData(
    strategy: StrategyForEditorOptimizeClicks
): StrategyOptimizeClicks {
    return {
        ...strategy,
        avgClickPrice: strategy.avgClickPrice.numVal,
        weekBundle: strategy.weekBundle.numVal,
        maxClickPrice: strategy.maxClickPrice.numVal,
        weeklyBudget: strategy.weeklyBudget.numVal
    };
}

export function mapStrategyForEditor(strategy: Strategy): StrategyForEditor {
    switch (strategy.type) {
        case DnaStrategyType.MANUAL:
            return mapStrategyManualForEditor(strategy);

        case DnaStrategyType.ROI:
            return mapStrategyRoiForEditor(strategy);

        case DnaStrategyType.OPTIMIZE_CLICKS:
            return mapStrategyOptimizeClicksForEditor(strategy);

        case DnaStrategyType.OPTIMIZE_CPA:
            return mapStrategyOptimizeCpaForEditor(strategy);

        default:
            throw new Error('unsupported strategy type');
    }
}

export function mapStrategyForCampaignData(strategy: StrategyForEditor): Strategy {
    switch (strategy.type) {
        case DnaStrategyType.MANUAL:
            return mapStrategyManualForCampaignData(strategy);

        case DnaStrategyType.ROI:
            return mapStrategyRoiForCampaignData(strategy);

        case DnaStrategyType.OPTIMIZE_CLICKS:
            return mapStrategyOptimizeClicksForCampaignData(strategy);

        case DnaStrategyType.OPTIMIZE_CPA:
            return mapStrategyOptimizeCpaForCampaignData(strategy);

        default:
            throw new Error('unsupported strategy type');
    }
}

export function mapCampaignsDataForEditToInitStrategyData(
    campaigns: Record<CampaignId, EditCampaignData>
): StrategyEditorInitData[] {
    const result: StrategyEditorInitData[] = [];

    Object.keys(campaigns).forEach((campaignId: CampaignId) => {
        const campaign: EditCampaignData = campaigns[campaignId];

        if (campaign.enabledFields.strategy) {
            result.push({
                editorId: getCampaignStrategyEditorId(campaignId),
                strategy: mapStrategyForEditor(campaign.value[EditCampaignValueField.strategy]),
                isChooserDisabled: checkIsStrategyChooserDisabled(campaign.type)
            });
        }
    });

    return result;
}

const checkIsStrategyChooserDisabled = (campaignType: CampaignType): boolean => {
    switch (campaignType) {
        case CampaignType.CONTENT_PROMOTION:
            return true;
        default:
            return false;
    }
};

export function mapStrategyGoalsFromApiResponse(
    response: FetchResult<GetOrCreateMetrikaGoalsByCounter>
): [Record<string, GoalForStrategy>, Record<string, GoalForStrategy>, boolean] {
    if (!response.data) {
        return [{}, {}, false];
    }

    const data = response.data.getOrCreateMetrikaGoalsByCounter;

    return [
        (data.goals || []).reduce((acc, goal): Record<string, GoalForStrategy> => {
            acc[goal.id] = goal;

            return acc;
        }, {}),
        (data.payForConversionGoals || []).reduce((acc, goal): Record<string, GoalForStrategy> => {
            acc[goal.id] = goal;

            return acc;
        }, {}),
        data.isMetrikaAvailable
    ];
}

export function mapStrategyGoalsDataForReducer(
    campaignIds: CampaignId[],
    metrikaGoalsData: Array<[Record<string, GoalForStrategy>, Record<string, GoalForStrategy>, boolean]>
): SetMetrikaGoalsDataForStrategyEditorActionPayload {
    return campaignIds.reduce((acc, campaignId, idx): SetMetrikaGoalsDataForStrategyEditorActionPayload => {
        acc[campaignId] = metrikaGoalsData[idx] || [{}, false];

        return acc;
    }, {});
}
